import React, { useRef, useState } from "react";
import { Image as ImageIcon, RefreshCw, Trash2, Upload, Link2, X } from "lucide-react";
import imageCompression from "browser-image-compression";
import { useAlert } from "context/AlertContext";
import { uploadSingleFile } from "services/upload-service/upload-service";

// Reusable single-image picker. Uploads the chosen file straight to S3 through
// the shared presigned-url upload service and hands the resulting public URL
// back via onChange — so callers only ever deal with a plain string URL.
//
// Two layouts:
//   variant="card"    → full-width dropzone / preview (banners, image blocks)
//   variant="compact" → small square thumbnail + button (gallery rows, lists)

const DEFAULT_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];

// Animated / vector formats must not go through the raster compressor.
const NON_COMPRESSIBLE = ["image/gif", "image/svg+xml"];

const ImageUploadField = ({
  value,
  onChange,
  directory = "knowledge-article",
  projectTitle = "knowledge-article-image",
  acceptedTypes = DEFAULT_TYPES,
  maxSizeMB = 10,
  compress = true,
  variant = "card",
  previewClassName = "h-40",
  emptyLabel = "Upload an image",
  helperText,
  allowUrl = true,
  disabled = false,
}) => {
  const { showAlert } = useAlert();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [urlMode, setUrlMode] = useState(false);

  const hint =
    helperText ??
    `${acceptedTypes
      .map((t) => t.replace("image/", "").replace("svg+xml", "svg").toUpperCase())
      .join(", ")} · up to ${maxSizeMB} MB`;

  const handleFile = async (file) => {
    if (!file || disabled) return;

    if (!acceptedTypes.includes(file.type)) {
      showAlert({ type: "warning", message: `${file.name} is not a supported image type.` });
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      showAlert({ type: "warning", message: `Image must be under ${maxSizeMB} MB.` });
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const shouldCompress = compress && !NON_COMPRESSIBLE.includes(file.type);
      const payload = shouldCompress
        ? await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 1600, useWebWorker: true })
        : file;

      const result = await uploadSingleFile({
        file: payload,
        directory,
        projectTitle,
        onProgress: setProgress,
      });

      if (!result.success) {
        if (!result.cancelled) {
          showAlert({ type: "error", message: "Image upload failed. Please try again." });
        }
        return;
      }
      onChange(result.file_url);
    } catch {
      showAlert({ type: "error", message: "Image processing failed. Please try a different image." });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const openPicker = () => inputRef.current?.click();

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={acceptedTypes.join(",")}
      className="hidden"
      onChange={(e) => {
        handleFile(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  );

  const urlToggle = allowUrl && (
    <button
      type="button"
      onClick={() => setUrlMode((prev) => !prev)}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
    >
      {urlMode ? <X size={11} /> : <Link2 size={11} />}
      {urlMode ? "Cancel link" : "Use a link instead"}
    </button>
  );

  const urlInput = allowUrl && urlMode && (
    <input
      className="w-full px-3 py-2 text-sm rounded-lg outline-none border border-border bg-transparent text-text_primary focus:border-border_strong focus:ring-2 focus:ring-border"
      placeholder="https://..."
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-2">
        {fileInput}
        <div className="flex-shrink-0 w-11 h-11 rounded-md bg-surface_alt overflow-hidden flex items-center justify-center border border-border">
          {uploading ? (
            <RefreshCw size={14} className="text-primary animate-spin" />
          ) : value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={15} className="text-text_muted" />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled || uploading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-[11px] font-semibold text-text_secondary bg-surface disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Upload size={11} />
              {uploading ? `${progress}%` : value ? "Replace" : "Upload"}
            </button>
            {value && !uploading && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="p-1.5 rounded-md text-danger"
                title="Remove image"
              >
                <Trash2 size={12} />
              </button>
            )}
            {urlToggle}
          </div>
          {urlInput}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {fileInput}

      {value && !uploading ? (
        <div className="relative rounded-xl overflow-hidden border border-border">
          <img src={value} alt="" className={`w-full ${previewClassName} object-cover`} />
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 px-3 py-2 bg-black/55">
            <span className="text-[11px] text-white font-medium truncate">Uploaded image</span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={openPicker}
                className="p-1.5 rounded-md bg-white/15 text-white"
                title="Replace image"
              >
                <RefreshCw size={13} />
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="p-1.5 rounded-md bg-white/15 text-white"
                title="Remove image"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
        >
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled || uploading}
            className={`w-full border-2 border-dashed rounded-xl p-6 text-center disabled:opacity-70 disabled:cursor-not-allowed ${
              dragOver ? "border-primary bg-primary/5" : "border-border bg-surface_alt/30"
            }`}
          >
            {uploading ? (
              <>
                <RefreshCw size={20} className="mx-auto text-primary animate-spin" />
                <p className="mt-2 text-[12px] font-medium text-text_secondary">Uploading… {progress}%</p>
              </>
            ) : (
              <>
                <ImageIcon size={20} className="mx-auto text-text_muted" />
                <p className="mt-2 text-[13px] font-medium text-text_secondary">{emptyLabel}</p>
                <p className="text-[11px] text-text_muted mt-0.5">{hint}</p>
              </>
            )}
          </button>
        </div>
      )}

      {(urlToggle || urlInput) && (
        <div className="space-y-2">
          {urlToggle}
          {urlInput}
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
